import type { PlausibilityMode } from "@sitescene/types";

import { useState } from "react";

import type { ShadowTenantSettingsUpdate } from "@/features/plausibility/use-plausibility-settings";

import { StatusMessage } from "@/components/status-message";
import { Toggle } from "@/components/toggle";
import { Button } from "@/components/ui/buttons/button";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	usePlausibilitySettings,
	useUpdatePlausibilitySettings,
} from "@/features/plausibility/use-plausibility-settings";

import { BackfillPanel } from "./BackfillPanel";
import { ReverifyPanel } from "./ReverifyPanel";

const MODES: { description: string; value: PlausibilityMode }[] = [
	{
		description:
			"The verifier ignores this organization. No rows, no model calls.",
		value: "off" as PlausibilityMode,
	},
	{
		description:
			"Every alert-worthy detection is verified and the outcome recorded. Measurements, events, and notifications are untouched.",
		value: "shadow" as PlausibilityMode,
	},
	{
		description:
			"Verification rewrites measurement severity. Notifications for 5+ events are held up to two minutes for the verdict; a down-rank under the alert line sends nothing.",
		value: "live" as PlausibilityMode,
	},
];

/**
 * Per-organization plausibility settings: the verification mode and the
 * footage check, two independent switches saved together.
 *
 * Keyed by organization: switching organizations starts the page afresh, so
 * an unsaved edit (or a backfill preview) never carries over to the next one,
 * where Save would write it.
 */
export const PlausibilitySettings = () => {
	const { selected } = useOrgSelection();
	return <OrganizationSettings key={selected?.orgCode ?? ""} />;
};

const OrganizationSettings = () => {
	const settings = usePlausibilitySettings();
	const update = useUpdatePlausibilitySettings();
	const [modeDraft, setModeDraft] = useState<null | PlausibilityMode>(null);
	const [claimsDraft, setClaimsDraft] = useState<boolean | null>(null);

	const currentMode =
		settings.data?.plausibilityMode ?? ("off" as PlausibilityMode);
	const currentClaims = settings.data?.claimsCheckEnabled === true;
	const mode = modeDraft ?? currentMode;
	const claims = claimsDraft ?? currentClaims;

	// Only what differs from the saved settings goes out.
	const changes: ShadowTenantSettingsUpdate = {
		...(mode !== currentMode && { plausibilityMode: mode }),
		...(claims !== currentClaims && { claimsCheckEnabled: claims }),
	};
	const dirty = Object.keys(changes).length > 0;

	return (
		<div className="max-w-xl">
			<form
				onSubmit={(event) => {
					event.preventDefault();
					update.mutate(changes, {
						onSuccess: () => {
							setModeDraft(null);
							setClaimsDraft(null);
						},
					});
				}}
			>
				<div className="flex items-start justify-end gap-4">
					{settings.data && (
						<div className="flex items-center gap-3">
							{update.isSuccess && !dirty && (
								<span className="text-sm text-secondary">Saved.</span>
							)}
							<Button
								isDisabled={update.isPending || !dirty}
								size="sm"
								type="submit"
							>
								{update.isPending ? "Saving…" : "Save"}
							</Button>
						</div>
					)}
				</div>
				<StatusMessage
					error={settings.error}
					isLoading={settings.isLoading}
					loading="Loading settings…"
				/>
				{update.error && (
					<p className="mt-2 text-sm text-error-primary" role="alert">
						{update.error.message}
					</p>
				)}
				{settings.data && (
					<>
						<section className="mt-6 flex flex-col gap-3 border-t border-secondary pt-6">
							<div>
								<h3 className="text-sm font-semibold text-primary">
									Verification mode
								</h3>
								<p className="text-sm text-secondary">
									What happens to the verifier's own decision on each alert.
								</p>
							</div>
							{MODES.map((option) => (
								<label
									className="flex items-start gap-2 text-sm"
									key={option.value}
								>
									<input
										checked={mode === option.value}
										className="mt-1"
										name="plausibilityMode"
										onChange={() => setModeDraft(option.value)}
										type="radio"
										value={option.value}
									/>
									<span>
										<span className="font-medium text-primary">
											{option.value}
										</span>
										<span className="block text-xs text-secondary">
											{option.description}
										</span>
									</span>
								</label>
							))}
						</section>
						<section className="mt-6 flex flex-col gap-3 border-t border-secondary pt-6">
							<div>
								<h3 className="text-sm font-semibold text-primary">
									Footage check
								</h3>
								<p className="text-sm text-secondary">
									Each alert's description and the camera's rules are checked
									against its footage, whatever the verification mode.
									Corrections, rule floors and lowered severities apply to the
									alert.
								</p>
							</div>
							<Toggle
								hint="About $0.05 per checked alert. Off: camera rules are not shown to the first pass."
								isSelected={claims}
								label="Check descriptions and camera rules against the footage"
								onChange={setClaimsDraft}
							/>
						</section>
					</>
				)}
			</form>
			{settings.data && currentMode !== ("off" as PlausibilityMode) && (
				<div className="mt-8 flex flex-col gap-4">
					<BackfillPanel />
					<ReverifyPanel />
				</div>
			)}
		</div>
	);
};
